import { useEffect, useRef, useState } from "react";
import { Baby, Eye } from "lucide-react";

import {
  ActionCard,
  InlineMessage,
  MonitorPage,
  PageHeading,
} from "@/components/monitor/MonitorUI";
import type { DeviceType } from "@/types";
import styles from "./DeviceSelectionPage.module.scss";

interface DeviceSelectionPageProps {
  onSelect: (type: DeviceType) => Promise<boolean>;
  initialError?: string | null;
}

export function DeviceSelectionPage({
  onSelect,
  initialError = null,
}: DeviceSelectionPageProps) {
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedType, setSelectedType] = useState<DeviceType | null>(null);
  const [error, setError] = useState<string | null>(initialError);
  const pendingRef = useRef(false);

  useEffect(() => {
    if (initialError) setError(initialError);
  }, [initialError]);

  const handleSelect = async (type: DeviceType) => {
    if (pendingRef.current) return;

    pendingRef.current = true;
    setSelectedType(type);
    setIsSelecting(true);
    setError(null);

    try {
      const success = await onSelect(type);
      if (!success) {
        setError("We couldn't set up this mode. Please try again.");
        setSelectedType(null);
      }
    } catch (err) {
      console.error("Device mode selection error:", err);
      setError("We couldn't set up this mode. Please try again.");
      setSelectedType(null);
    } finally {
      pendingRef.current = false;
      setIsSelecting(false);
    }
  };

  return (
    <MonitorPage className={styles.page}>
      <PageHeading
        className={styles.heading}
        title="How will you use this device?"
        description="Another device signed in to the same account uses the other mode."
      />

      {error ? <InlineMessage>{error}</InlineMessage> : null}

      <div className={styles.cards}>
        <ActionCard
          actionIcon="arrow"
          icon={<Baby size={26} strokeWidth={1.9} />}
          label="Baby Mode"
          description="Place this device near your baby."
          onClick={() => void handleSelect("BABY")}
          disabled={isSelecting}
          loading={isSelecting && selectedType === "BABY"}
          loadingLabel="Setting up…"
        />
        <ActionCard
          actionIcon="arrow"
          icon={<Eye size={26} strokeWidth={1.9} />}
          label="Parent Mode"
          description="Watch and listen from this device."
          tone="sage"
          onClick={() => void handleSelect("PARENT")}
          disabled={isSelecting}
          loading={isSelecting && selectedType === "PARENT"}
          loadingLabel="Setting up…"
        />
      </div>

      <p className={styles.note}>You can change this mode later in Settings.</p>
    </MonitorPage>
  );
}
